import * as React from 'react';
import { tableName, columnName, sectionId } from 'app/area/case/case-data-model';
import { CompositeKeys, Entity } from 'app/types-shared';
import { getPrimaryEntityId, getEntityTypeFromStorePath } from 'app/infrastructure/store/entity';
import { getValue } from 'app/infrastructure/ui/form/selectors';
import { TileViewProps } from 'ui/tile-view';
import { TileView } from 'ui';
import { AddDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import { ComponentDescriptor } from 'app/infrastructure/ui/types-uischema';
import styles from './loss-potential-evaluation-tile-view.scss';
import { RiskEvaluationTimeline } from './risk-evaluation-timeline/risk-evaluation-timeline';
import { RiskEvaluationTimelineItem } from 'app/area/case/components/loss-potential/risk-evaluation-timeline/types';
import { AddLossPotentialModal } from 'app/area/case/components/loss-potential/add-loss-potential-evaluation-modal';
import * as navigate from 'app/area/shared/navigation';
import { routeConstants } from 'app/routes/route-constants';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { AlertInfo } from 'app/components/alert-info/alert-info';
import { useQueryParams } from 'app/area/shared/navigation';
import { DefaultContainerProps } from 'app/infrastructure/redux';

export interface LossPotentialEvaluationTileViewProps extends TileViewProps, DefaultContainerProps {
  componentDescriptor: ComponentDescriptor;
  onSelectDocumentItem?: (path?: any) => void;
  onAddDocumentItem: (selectAddedItem?: boolean, options?: AddDocumentItemOptions) => void;
  childEntities: Entity[];
  expandAll?: boolean;
  navigateToUrl?: boolean;
  displayRiskEvaluationTimeline?: boolean;
  hideComponent?: boolean;
  getUiDefinition?: () => void;
  onCopyRiskEvaluation?: (riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, 
    newWindow?: boolean, copyToResidual?: boolean) => void;
  noOfRowsResidual?: number;
  textEvaluationNo?: string;
  textAddedToLossPotential?: string;
  textAddedToResidual?: string;
  textCompleteSingleEvaluation?: string;
  textCompleteMultipleEvaluations?: string;
  hideRiskWeight?: boolean;
}
interface LossPotentialEvaluationTileViewState {
  displayInfoMessageAddEvaluation?: boolean;
}

export const LossPotentialEvaluationTileView = (props: LossPotentialEvaluationTileViewProps & LossPotentialEvaluationTileViewState) => {
  const dateIntlOption: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const addLossPotential = !!useQueryParams().query.addLossPotential;
  const addResidual = !!useQueryParams().query.addResidual;
  const [displayInfoMessageAddEvaluation, setDisplayInfoMessageAddEvaluation] = React.useState(true);

  const changeSelectedEvaluation = (item: RiskEvaluationTimelineItem) => {
    const { onSelectDocumentItem, childEntities } = props;
    onSelectDocumentItem && childEntities &&
      onSelectDocumentItem(childEntities.find(c => JSON.stringify(c.id) === JSON.stringify(item?.entityPath)).id);
    item?.url && navigate.to({ pathname: getEvaluationUrl(item.entityPath, item.primaryKey) }, { state: { internal: true } });
  };

  const getMatchingId = () => {
    const { childEntities } = props;
    const { activeChildItem } = (props as any).hostProps();

    if (!childEntities || childEntities.length === 0) {
      return '';
    }

    // By default we select the last item if a descendant is not active.
    const defaultMatchingId = childEntities[childEntities.length - 1].id;

    // 1. There is no active child item or it is the top level item.
    if (!activeChildItem || activeChildItem.length === 1) {
      return defaultMatchingId;
    }

    // Select those path elements that might be shared for later comparison.
    const partialPath = activeChildItem.slice(0, defaultMatchingId.length);

    const activeType = getEntityTypeFromStorePath(partialPath);
    const thisType = getEntityTypeFromStorePath(defaultMatchingId);

    // 2. The active child item might be a descendant.
    if (activeType === thisType) {
      return partialPath;
    }

    return defaultMatchingId;
  };

  const getEntityCaseNumber = (entityPath: CompositeKeys) => {
    return entityPath[0]?.tblTrans;
  };

  const getEntityTableName = (entityPath: CompositeKeys) => {
    return Object.keys(entityPath[1])?.toString();
  };

  const getEvaluationUrl = (entityPath: CompositeKeys, primaryKey: number) => {
    const targetSectionNumber = getEntityTableName(entityPath) === tableName.lossPotentialEvaluation ? 
      sectionId[tableName.lossPotentialEvaluation].section :
      sectionId[tableName.lossPotentialResidualEvaluation].section;
    const entityPathName = getEntityCaseNumber(entityPath) + '/' + getEntityTableName(entityPath) + '/' + primaryKey;
    
    return routeConstants.caseViewEvaluation(entityPathName, targetSectionNumber);
  };

  const { childEntities, formatDate, componentDescriptor, children, className, navigateToUrl, displayRiskEvaluationTimeline = true, 
    hideComponent, textEvaluationNo, textAddedToLossPotential, textAddedToResidual, textCompleteSingleEvaluation, 
    textCompleteMultipleEvaluations, noOfRowsResidual, hideRiskWeight } = props;
  const { entityTypePath } = componentDescriptor;
  let timelineElement = undefined;
  let infoMessage = undefined;

  if (childEntities && childEntities.length > 0) {
    const validFromFieldName = (columnName[entityTypePath[entityTypePath.length - 1]] as { validFrom: string }).validFrom;
    const riskAreaFieldName = (columnName[entityTypePath[entityTypePath.length - 1]] as { riskArea: string }).riskArea;
    const riskWeightFieldName = (columnName[entityTypePath[entityTypePath.length - 1]] as { riskWeight: string }).riskWeight;
    const riskTrendFieldName = (columnName[entityTypePath[entityTypePath.length - 1]] as { riskTrend: string }).riskTrend;

    const values = childEntities.map(child => {
      const pk = getPrimaryEntityId(child);
      const validFrom = getValue(child, validFromFieldName).value;
      const riskArea = getValue(child, riskAreaFieldName).valueItem;
      const riskWeight = getValue(child, riskWeightFieldName).value;
      const riskTrend = getValue(child, riskTrendFieldName).valueItem;
      const filled = child.id[entityTypePath.length - 1]?.hasOwnProperty(tableName.lossPotentialEvaluation);
      
      return { 
        primaryKey: pk, 
        entityPath: child.id,
        date: formatDate(validFrom as Date, dateIntlOption), 
        text: riskWeight,
        filled: filled,
        tooltip: riskArea?.description || '',
        backgroundColor: riskArea?.backgroundColor || undefined,
        foregroundColor: filled && riskArea?.foregroundColor,
        riskTrend: {value: Number(riskTrend?.value), description: riskTrend?.description, icon: riskTrend?.icon},
        url: navigateToUrl && getEvaluationUrl(child.id, pk)
      };      
    });

    timelineElement = <RiskEvaluationTimeline 
                        id={`timeline-evaluation-${getEntityTableName(values[0]?.entityPath)} -
                          ${values[0].url && getEntityCaseNumber(values[0]?.entityPath)}`}
                        items={values}
                        selectedItem={getMatchingId()}
                        onClick={changeSelectedEvaluation}
                        showTooltip={false}
                        shouldAnimateOnAdd={displayInfoMessageAddEvaluation}
                        hideRiskWeight={hideRiskWeight}
                      />;
    
    const lastPk = childEntities?.[childEntities.length-1]?.primaryKey.value;

    const infoMessageLossPotentialAndResidual = getEntityTableName(values[0]?.entityPath).toString() === tableName.lossPotentialEvaluation ?
      <AlertInfo text={ textEvaluationNo + ' ' + lastPk + ' ' + textAddedToLossPotential + '\r\n' + 
        textEvaluationNo + ' ' + noOfRowsResidual?.toString() + ' ' + textAddedToResidual + '\r\n' + textCompleteMultipleEvaluations } /> : 
      <AlertInfo text={textEvaluationNo + ' ' + lastPk + ' ' + textAddedToResidual + '\r\n' + textCompleteSingleEvaluation} />;

    const infoMessageLossPotentialOnly = getEntityTableName(values[0]?.entityPath).toString() === tableName.lossPotentialEvaluation ?
      <AlertInfo text={ textEvaluationNo + ' ' + lastPk + ' ' + textAddedToLossPotential + '\r\n' + textCompleteSingleEvaluation } /> : 
        undefined;
    
    infoMessage = addLossPotential && addResidual ? infoMessageLossPotentialAndResidual : 
      addLossPotential ? infoMessageLossPotentialOnly : undefined;
  }

  const showAddEvaluationButton = !props.readOnly && props.enabledChildEntityOperations?.add;

  const addEvaluationButton = showAddEvaluationButton && <AddLossPotentialModal {...props} />;

  const onClickInside = () => {
    setDisplayInfoMessageAddEvaluation(false);
  };

  return (
    !hideComponent ?
      <TileView {...props} rowClassName={`col-12 ${styles.lossPotTileView}`} className={`${className}`}>
        <div onClick={onClickInside}>
          {displayRiskEvaluationTimeline && <div className={`${styles.timelineWrapper} row col-12`}>
            {displayInfoMessageAddEvaluation && infoMessage}
            {timelineElement}
            {addEvaluationButton}
          </div>}
          {children}
        </div>
      </TileView>
      : null
  );
};
